import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSignals } from '@preact/signals-react/runtime';
import { useComputed, useSignalEffect } from '@preact/signals-react';
import produce from 'immer';
import { useAiComponentContext } from '@/features/editor/widgets/shared/ai-editing-layout/context';
import { EditorContext } from '@/features/editor/context/editor-context';
import { getQueryParam } from '@/utils/browser';
import { DeviceType } from '@/utils/definitions';
import { AiComponentTab } from '@/features/editor/widgets/shared/ai-editing-layout/types';
import { usePopConfirmPortal } from '@/features/editor/widgets/shared/use-pop-confirm-portal';
import { DEVICE_SWITCHER_ID } from '@/features/editor/preview-navigation';
import { TargetingDeviceType } from '@/webapi/use-experience-api';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';

const TABS = [`HTML`, `CSS`, `JS`];

export function useTabContainerState() {
  useSignals();
  const {
    devicePreview: {
      editorState: { device },
      setPreviewDevice,
    },
    experienceState: { getCurrentVariant },
    experienceControls: { previewTemp },
  } = useContext(EditorContext);

  const newChange = useAiComponentContext((ctx) => ctx.newChange);
  const hasChanges = useAiComponentContext((ctx) => ctx.hasChanges);
  const currentSessionId = useAiComponentContext((ctx) => ctx.sessionId);
  const firstSessionId = useAiComponentContext((ctx) => ctx.firstSessionId);
  const lastRunId = useAiComponentContext((ctx) => ctx.lastRunId);
  const hasVisualEditManifest = useAiComponentContext(
    (ctx) => ctx.hasVisualEditManifest,
  );
  const hasVisualElements = useAiComponentContext(
    (ctx) => ctx.hasVisualElements,
  );
  const hasLogicElements = useAiComponentContext((ctx) => ctx.hasLogicElements);
  const isBuilder = useAiComponentContext(
    (ctx) => ctx.selectedAgent,
  )?.value?.id?.includes(`widget-developer`);
  const selectedTab = useAiComponentContext((ctx) => ctx.selectedTab);
  const selectTab = useAiComponentContext((ctx) => ctx.selectTab);
  const setBackToTab = useAiComponentContext((ctx) => ctx.setBackToTab);
  const selectedCodeTab = useAiComponentContext((ctx) => ctx.selectedCodeTab);
  const onSelectedCodeTab = useAiComponentContext(
    (ctx) => ctx.onSelectedCodeTab,
  );
  const isFullscreen = useAiComponentContext((ctx) => ctx.isFullScreen);
  const isComponentReady = useAiComponentContext((ctx) => ctx.isComponentReady);
  const discardChanges = useAiComponentContext((ctx) => ctx.discardChanges);
  const applyChanges = useAiComponentContext((ctx) => ctx.applyChanges);
  const discardLocalChanges = useAiComponentContext(
    (ctx) => ctx.discardLocalChanges,
  );
  const restoreCheckpoint = useAiComponentContext(
    (ctx) => ctx.restoreCheckpoint,
  );
  const saveLocalChanges = useAiComponentContext((ctx) => ctx.saveLocalChanges);
  const invalidateRender = useAiComponentContext((ctx) => ctx.invalidateRender);
  const editorConfig = useAiComponentContext((ctx) => ctx.editorConfig);

  const { portalElement } = usePopConfirmPortal({
    targetElementId: DEVICE_SWITCHER_ID,
    enabled: newChange.value.targetDevice !== TargetingDeviceType.ALL_DEVICES,
    text: `By clicking “Yes”, the component’s targeting will be changed to all devices.`,
    onConfirm: async () => {
      newChange.value = produce(newChange.value, (draft) => {
        draft.targetDevice = TargetingDeviceType.ALL_DEVICES;
      });
      invalidateRender();
      setPreviewDevice(
        device === DeviceType.Desktop ? DeviceType.Mobile : DeviceType.Desktop,
      );
    },
    onDiscard: () => {
      // User cancelled the device switch
    },
  });

  const isHistoryEnabled = useFeatureBit(FeatureBit.SHOW_AI_HISTORY);

  const tabs = useMemo(() => {
    if (getQueryParam(`debug-ai`) === `1`) {
      return [...TABS, `M`];
    }
    return TABS;
  }, []);

  const wrapperClasses = useComputed(() => {
    const classes = [];
    if (isFullscreen.value) {
      classes.push(`fullscreen`);
    }
    if (
      device === `mobile` &&
      (selectedTab.value === AiComponentTab.EDIT ||
        selectedTab.value === AiComponentTab.LOGIC)
    ) {
      classes.push(`mobile-visual-edit`);
    }
    return classes.join(` `);
  });

  const outOfChat = useComputed(
    () => selectedTab.value !== AiComponentTab.PROMPT,
  );

  const visualEditBtnState = useComputed(() => {
    const cls = [];
    if (selectedTab.value === AiComponentTab.EDIT) {
      cls.push(`selected`);
    }
    if (selectedTab.value === AiComponentTab.CODE) {
      cls.push(`light`);
    }
    return cls.join(` `) || ``;
  });

  const logicBtnState = useComputed(() => {
    const cls = [];
    if (selectedTab.value === AiComponentTab.LOGIC) {
      cls.push(`selected`);
    }
    if (selectedTab.value === AiComponentTab.CODE) {
      cls.push(`light`);
    }
    return cls.join(` `) || ``;
  });

  useEffect(() => {
    const handler = (ev) => {
      if (ev.data === `#edit-visual`) {
        selectTab(AiComponentTab.EDIT);
      } else if (ev.data === `#edit-logic`) {
        selectTab(AiComponentTab.LOGIC);
      } else if (ev.data === `#edit-code`) {
        selectTab(AiComponentTab.CODE);
      } else if (ev.data?.includes?.(`#edit-strategy-`)) {
        selectTab(AiComponentTab.LOGIC);
        setBackToTab(AiComponentTab.PROMPT);
        setTimeout(() => {
          const buttonId = ev.data.replace(`#edit-strategy-`, `edit-strategy-`);
          const btn = document.getElementById(buttonId);
          btn?.click?.();
        }, 500);
      }
    };

    window?.addEventListener?.(`message`, handler);

    return () => window?.removeEventListener?.(`message`, handler);
  }, []);

  useEffect(() => {
    const lastTab = sessionStorage.getItem(`selectedTab`);
    if (lastTab) {
      selectTab(lastTab as AiComponentTab);
    }
    const lastCodeTab = sessionStorage.getItem(`selectedCodeTab`);
    if (lastCodeTab) {
      onSelectedCodeTab(lastCodeTab as 'HTML' | 'CSS' | 'JS');
    }
    return () => {
      sessionStorage.removeItem(`selectedTab`);
      sessionStorage.removeItem(`selectedCodeTab`);
    };
  }, []);

  useSignalEffect(() => {
    sessionStorage.setItem(`selectedTab`, selectedTab.value);
    sessionStorage.setItem(`selectedCodeTab`, selectedCodeTab.value);
  });

  const [isCheckpointOpen, setIsCheckpointOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  return {
    device,
    portalElement,
    isHistoryEnabled,
    tabs,
    wrapperClasses,
    outOfChat,
    visualEditBtnState,
    logicBtnState,
    isCheckpointOpen,
    setIsCheckpointOpen,
    wrapperRef,
    newChange,
    hasChanges,
    currentSessionId,
    firstSessionId,
    lastRunId,
    hasVisualEditManifest,
    hasVisualElements,
    hasLogicElements,
    isBuilder,
    selectedTab,
    selectTab,
    selectedCodeTab,
    onSelectedCodeTab,
    isFullscreen,
    isComponentReady,
    discardChanges,
    applyChanges,
    discardLocalChanges,
    restoreCheckpoint,
    saveLocalChanges,
    editorConfig,
    previewTemp,
    getCurrentVariant,
  };
}
